import { getUserToken } from '@/auth/streamTokenProvider';
import { AuthProgressLoader } from '@/components/auth/auth-progress-loader';
import { NoConversationSetupYet } from '@/components/custom-chat/no-conversation-setup-yet';
import { ThemeContext } from '@/context/theme-context';
import { getEnvVariables } from '@/env/getEnvVariables';
import { convertAppThemeToStreamChatTheme } from '@/utils/convertAppThemeToStreamChatTheme';
import { ChatCredentialsWithChannelInfoResult } from '@my-ipractice/types';
import { StreamVideo, StreamVideoClient } from '@stream-io/video-react-sdk';
import { PropsWithChildren, useContext, useEffect } from 'react';
import { User } from 'stream-chat';
import { Chat, Streami18n, useCreateChatClient } from 'stream-chat-react';
import SentryService from '@/utils/sentry';
import { useTranslation } from 'react-i18next';

export const AuthenticatedStreamWrapper = ({
  children,
  chatCredentials,
}: PropsWithChildren<{
  chatCredentials: ChatCredentialsWithChannelInfoResult;
}>) => {
  const { theme } = useContext(ThemeContext);
  const { i18n } = useTranslation();

  const streami18n = new Streami18n({
    language: i18n.language,
  });

  const apiKey = getEnvVariables().streamKey;
  const user: User = { id: chatCredentials.userId };

  const chatClient = useCreateChatClient({
    apiKey: apiKey,
    tokenOrProvider: getUserToken,
    userData: user,
  });

  const videoClient = StreamVideoClient.getOrCreateInstance({
    apiKey,
    user,
    tokenProvider: getUserToken,
    options: { logLevel: 'error' },
  });

  // Set Sentry user context when stream user ID is available
  useEffect(() => {
    try {
      SentryService.setUserContext(chatCredentials.userId);
    } catch (error) {
      SentryService.addBreadcrumb(`Failed to set Sentry user context: ${error}`, 'error');
    }
  }, [chatCredentials.userId]);

  if (!chatCredentials.channelId) {
    return <NoConversationSetupYet />;
  }

  if (!chatClient) {
    return <AuthProgressLoader />;
  }

  return (
    <Chat
      client={chatClient}
      i18nInstance={streami18n}
      theme={convertAppThemeToStreamChatTheme(theme)}
    >
      <StreamVideo client={videoClient}>{children}</StreamVideo>
    </Chat>
  );
};
